import { JSX } from "react";
import { Navigate } from "react-router-dom";
import AppTransferOrderForm from "../components/Layouts/AppTransferOrderForm/AppTransferOrderForm";
import { AppBasePage } from "../components/Layouts/BasePage/BasePage";
import CreateProductTemplateOrder from "../components/UI/CreateOrder/CreateProductTemplateOrder";
import CreatePurchaseOrderPage from "../components/UI/CreateOrder/CreatePurchaseOrderPage";
import CreateTransferOrder from "../components/UI/CreateOrder/CreateTransferOrder";
import EditPurchaseOrder from "../components/UI/CreateOrder/EditPurchaseOrder";
import NewDeliveryOrderForm from "../components/UI/CreateOrder/NewDeliveryOrderForm";
import {
  default as SalesOrder,
  default as SalesOrderPage,
} from "../components/UI/CreateOrder/SalesOrder";
import SalesOrderHistory from "../components/UI/CreateOrder/SalesOrderHistory";
import ReturnOrderPage from "../components/UI/CreateOrder/SalesOrderReturn";
import DeliveryOrdersPage from "../components/UI/DeliveryOrder/DeliveryOrder";
import UIDPartCodeMapping from "../components/UI/UIDPartCodeMapping/UIDPartCodeMapping";
// import DeliveryOrderHistoryPage from "../components/UI/DeliveryOrder/DeliveryOrderHistory";
import ActionHistory from "../components/UI/ActionHistory/ActionHistory";
import BackOrderReport from "../components/UI/AllReportsDownload/BackOrderReport";
import CreatedProductReport from "../components/UI/AllReportsDownload/CreateProductReport";
import DOReport from "../components/UI/AllReportsDownload/DeliveryOrderReport";
import POReport from "../components/UI/AllReportsDownload/PurchaseOrderReport";
import SalesOrderReport from "../components/UI/AllReportsDownload/SalesOrderReport";
import TransferOrderReport from "../components/UI/AllReportsDownload/TransferOrderReport";
import AllReturnOrders from "../components/UI/AllReturnOrder/AllReturnOrder";
import AllTransferReturnOrders from "../components/UI/AllReturnOrder/TransferOrderReturn";
import ApprovalList from "../components/UI/ApprovalList/ApprovalList";
import ViewApprovalList from "../components/UI/ApprovalList/ViewApprovalList";
import CreateDeliveryOrderDraft from "../components/UI/CreateOrder/CreateDeliveryOrderDraft";
import CreateSalesOrder from "../components/UI/CreateOrder/CreateSalesOrder";
import CreateVendor from "../components/UI/CreateOrder/CreateVendor";
import CreateWarehouse from "../components/UI/CreateOrder/CreateWarehouse";
import CustomerTemplate from "../components/UI/CustomerTemplate/CustomerTemplate";
import ViewDeliveryOrderDetails from "../components/UI/DeliveryOrder/ViewDeliveryOrderDetails";
import InventoryList from "../components/UI/Inventory/InventoryList";
import InventoryHistoryReport from "../components/UI/Inventory/InventoryHistoryReport";
import LoginScreen from "../components/UI/Login/Login";
import PartDetailsByIdPage from "../components/UI/ProductTemplate/PartDetailsById";
import PartFamily from "../components/UI/ProductTemplate/PartFamily";
import ProductTemplate from "../components/UI/ProductTemplate/ProductTemplate";
import PurchaseOrderPage from "../components/UI/PurchaseOrder/PurchaseOrder";
import ResetPassword from "../components/UI/ResetPassword/ResetPassword";
import ReturnToVendor from "../components/UI/ReturnToVendor/ReturnToVendor";
import ViewSalesOrder from "../components/UI/SalesOrder/ViewSalesOrder";
import ViewSalesOrderReturn from "../components/UI/SalesOrder/ViewSalesOrderReturn";
import SpareForecast from "../components/UI/SpareForecast/SpareForecast";
import CancelTransferOrder from "../components/UI/TransferOrder/CancelTransferOrder";
import CompletedTransferOrder from "../components/UI/TransferOrder/CompletedTransferOrder";
import CreateTransferOrderComplete from "../components/UI/TransferOrder/CreateTransferOrderComplete";
import DraftTransferOrder from "../components/UI/TransferOrder/DraftTransferOrder";
import InTransitTransferOrder from "../components/UI/TransferOrder/InTransitTransferOrder";
import PickupTransferOrder from "../components/UI/TransferOrder/PickUpTransferOrder";
import ReturnToSourceTransferOrder from "../components/UI/TransferOrder/ReturnToSource";
import TransferOrderPage from "../components/UI/TransferOrder/TransferOrder";
import CreateUser from "../components/UI/UserManagement/CreateUser";
import EditUser from "../components/UI/UserManagement/EditUser";
import {
  default as Vendor,
  default as VendorListPage,
} from "../components/UI/Vendor/Vendor";
import ViewPurchaseOrder from "../components/UI/Views/ViewPurchaseOrder";
import PartBinMapping from "../components/UI/Warehouse/PartBinMapping";
import WarehouseListPage from "../components/UI/Warehouse/Warehouse";
import WarehouseBinsPage from "../components/UI/Warehouse/WarehouseBins";
import WarehouseMapping from "../components/UI/Warehouse/WarehouseMapping";
import WarrantyRequests from "../components/UI/WarrantyRequests/WarrantyRequests";
import { Routes } from "../states/States";
import ERPDashboard from "../views/Homepage/ERPDashboard";
import MainLayout from "../views/MainLayout/MainLayout";

const allowedAuthorities = [
  "ROLE_ERP_ADMIN",
  "ROLE_ERP_MANAGER",
  "ROLE_ERP_IT_ADMIN",
  "ROLE_INVENTORY_MANAGER",
  "ROLE_ERP_DATA",
  "ROLE_ERP_REFURB_VENDOR",
  "ROLE_ERP_WARRANTY_VENDOR",
  "ROLE_FRANCHISE_INVENTORY_MANAGER",
  "ROLE_ERP_FRANCHISE_ADMIN"
];

const RequireAuth = ({ children }: { children: JSX.Element }) => {
  const isAuthenticated = localStorage.getItem("token");
  const authority = localStorage.getItem("authority");

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  if (!allowedAuthorities.includes(authority || "")) {
    localStorage.removeItem("token");
    localStorage.removeItem("authority");
    localStorage.removeItem("name");
    return <Navigate to="/" replace />;
  }
  return children;
};

const HomeRoute = () => {
  const authority = localStorage.getItem("authority");

  if (authority === "ROLE_ERP_IT_ADMIN") {
    return <Navigate to="/user-management/create" replace />;
  }

  if (authority === "ROLE_FRANCHISE_INVENTORY_MANAGER") {
    return <Navigate to="/sales-order" replace />;
  }

  return (
    <MainLayout>
      <AppBasePage>
        <ERPDashboard />
      </AppBasePage>
    </MainLayout>
  );
};

const CommonRoutes: Array<Routes> = [
  {
    path: "/",
    element: <LoginScreen />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
  },
  {
    path: "/home",
    element: (
      <RequireAuth>
        <HomeRoute />
      </RequireAuth>
    ),
  },
  {
    path: "/uid-part-code-mapping",
    element: (
      <RequireAuth>
        <MainLayout>
          <UIDPartCodeMapping />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/create-order",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <CreatePurchaseOrderPage />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/purchase-orders/edit/:id",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <EditPurchaseOrder />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/purchase-order/view/:id",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <ViewPurchaseOrder />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/approval-list",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <ApprovalList />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/approval-request/view/:id",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <ViewApprovalList />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: `/purchase-order`,
    element: (
      <RequireAuth>
        <MainLayout>
          <PurchaseOrderPage />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: `/delivery-order`,
    element: (
      <RequireAuth>
        <MainLayout>
          <DeliveryOrdersPage />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/delivery-order/:id",
    element: (
      <RequireAuth>
        <MainLayout>
          <DeliveryOrdersPage />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/delivery-order/details/:id",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <ViewDeliveryOrderDetails />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/delivery-order/create",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <NewDeliveryOrderForm
              handleBackToOrders={() => {
                window.history.back();
              }}
            />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/delivery-order/edit/:id",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <CreateDeliveryOrderDraft
              onBack={() => {
                window.history.back();
              }}
              orderStage={""}
            />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },

  // {
  //   path: "/delivery-order-history",
  //   element: (
  //     <RequireAuth>
  //       <MainLayout>
  //         <DeliveryOrderHistoryPage />
  //       </MainLayout>
  //     </RequireAuth>
  //   ),
  // },
  {
    path: `/return-to-vendor/`,
    element: (
      <RequireAuth>
        <MainLayout>
          <ReturnToVendor
            onBack={() => {
              window.history.back();
            }}
          />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/user-management",
    element: (
      <RequireAuth>
        <Navigate to="/user-management/create" replace />
      </RequireAuth>
    ),
  },
  {
    path: "/user-management/create",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <CreateUser />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/user-management/edit",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <EditUser />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/user-management/edit/:userId",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <EditUser />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: `/delivery-return`,
    element: (
      <RequireAuth>
        <MainLayout>
          <AllReturnOrders />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: `/transfer-return`,
    element: (
      <RequireAuth>
        <MainLayout>
          <AllTransferReturnOrders />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/product-template",
    element: (
      <RequireAuth>
        <MainLayout>
          <ProductTemplate />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/part-family",
    element: (
      <RequireAuth>
        <MainLayout>
          <PartFamily />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/inventory-list",
    element: (
      <RequireAuth>
        <MainLayout>
          <InventoryList />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/transfer-order",
    element: (
      <RequireAuth>
        <MainLayout>
          <TransferOrderPage />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/vendor",
    element: (
      <RequireAuth>
        <MainLayout>
          <Vendor />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/history/order",
    element: (
      <RequireAuth>
        <MainLayout>
          <SalesOrderHistory />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/sales-order",
    element: (
      <RequireAuth>
        <MainLayout>
          <SalesOrder />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/sales-order/create",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <CreateSalesOrder />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/sales-order/view/:id",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <ViewSalesOrder />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/sales-order/return/view/:id",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <ViewSalesOrderReturn />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/transfer-order/draft/:orderId",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <DraftTransferOrder />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/transfer-order/:orderId",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <CreateTransferOrderComplete />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/transfer-order/create",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <CreateTransferOrder />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/transfer-order/picked/:orderId",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <PickupTransferOrder />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/transfer-order/in-transit/:orderId",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <InTransitTransferOrder />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/transfer-order/completed/:orderId",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <CompletedTransferOrder />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/transfer-order/cancelled/:orderId",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <CancelTransferOrder />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/transfer-order/return-to-source/:orderId",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <ReturnToSourceTransferOrder />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/transfer-order/form",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <AppTransferOrderForm />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/create/warehouse",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <CreateWarehouse
              onBack={function (): void {
                throw new Error("Function not implemented.");
              }}
            />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/create/parts",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <CreateProductTemplateOrder
              onBack={function (): void {
                throw new Error("Function not implemented.");
              }}
            />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/parts/:id",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <PartDetailsByIdPage />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/warehouse",
    element: (
      <RequireAuth>
        <MainLayout>
          <WarehouseListPage />
        </MainLayout>
      </RequireAuth>
    ),
  },
    {
    path: "/warehouse-mapping",
    element: (
      <RequireAuth>
        <MainLayout>
          <WarehouseMapping />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {    
    path: "/warehouse-bins",
    element: (
      <RequireAuth>
        <MainLayout>
          <WarehouseBinsPage />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/part-bin-mapping",
    element: (
      <RequireAuth>
        <MainLayout>
          <PartBinMapping />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/vendor",
    element: (
      <RequireAuth>
        <MainLayout>
          <VendorListPage />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/sales-order",
    element: (
      <RequireAuth>
        <MainLayout>
          <SalesOrderPage />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/return-order",
    element: (
      <RequireAuth>
        <MainLayout>
          <ReturnOrderPage />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/create-product/report",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <CreatedProductReport />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/inventory-history-report",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <InventoryHistoryReport />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/po-report",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <POReport />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/do-report",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <DOReport />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/back-order-report",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <BackOrderReport />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/to-report",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <TransferOrderReport />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/so-report",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <SalesOrderReport />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/customer-list",
    element: (
      <RequireAuth>
        <MainLayout>
          <CustomerTemplate />
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/create/vendor",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            {/* Use a wrapper to provide onBack */}
            <CreateVendor onBack={() => window.history.back()} />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/spare-forecast",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
            <SpareForecast />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/warranty-requests",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
          <WarrantyRequests />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
  {
    path: "/action-history",
    element: (
      <RequireAuth>
        <MainLayout>
          <AppBasePage>
          <ActionHistory />
          </AppBasePage>
        </MainLayout>
      </RequireAuth>
    ),
  },
];
export const Router: Array<Routes> = [
  ...CommonRoutes,
  /**
   *@description
   * Url to Embeded in Iframe
   *
   */
  // {
  //   path: "", // test route
  //   element: <Header />,
  //   children: [
  //     ...CommonRoutes,
  //     {
  //       path: "*", // test route
  //       element: <div>NOT FOUND</div>,
  //     },
  //   ],
  // },
];
